/**
 * List View - Phase Accordion with Task Cards
 *
 * Displays tasks organized by phase in accordion format:
 * - Foundation
 * - Standardization
 * - Automation
 *
 * Each task card shows:
 * - Title, description, implementation steps
 * - Priority, impact, effort, value
 * - Status toggle (not started → in progress → completed)
 * - FAIR rules and dependencies
 *
 * Design: Clean accordion with expandable task cards
 */

import React, { useState } from 'react';
import {
  anyFilterActive,
  matchesFilters,
} from './actionPlanMatrixPlacement';
import type { MatrixFilters } from './actionPlanMatrixPlacement';
import type { Task, Phase, Dependency, TaskStatus,
  TaskStatusPersisted } from '../types/actionPlan';
import { ViewCalculationPanel } from './ViewCalculationPanel';
import {
  EFFORT_UNAVAILABLE_LABEL,
  PROGRESS_UNAVAILABLE_LABEL,
  SCHEDULE_UNAVAILABLE_LABEL,
  formatCurrency,
  getStatusLabel,
  sumTaskValues,
} from './actionPlanValueFormat';

// ============================================================================
// COMPONENT PROPS
// ============================================================================

interface ListViewProps {
  tasks: Task[];
  phases: Phase[];
  dependencies: Dependency[];
  onTaskClick: (taskId: string) => void;
  onTaskStatusChange: (taskId: string, status: TaskStatus) => void;
  /**
   * The currently selected task's canonical Action Plan id, or null.
   *
   * Read from view/URL state by `ActionPlanPanel`, so a task stays selected when the
   * reader moves between views (S5 production). Selection was previously written to
   * component state and never read back, which is why it could not survive a switch.
   */
  selectedTaskId?: string | null;
  /** The plan-wide filter object (S4f-B) — consumed identically to the Matrix. */
  filters: MatrixFilters;
  onClearFilters: () => void;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

// Local `formatCurrency` removed: it returned '$0' for an absent value, the
// same defect in a sixth place. One shared implementation (P0-4).

const getPriorityColor = (priority: string): string => {
  switch (priority) {
    case 'critical': return '#dc2626'; // red
    case 'high': return '#f59e0b'; // orange
    case 'medium': return '#3b82f6'; // blue
    case 'low': return '#9ca3af'; // gray
    default: return '#9ca3af';
  }
};

/**
 * `blocked` and `skipped` are PERSISTED states (migration 021:137, retained by
 * 141), not settable ones. They must render as themselves: collapsing them into
 * the not-started glyph told the user a task was merely unstarted when the plan
 * recorded something different — a fabricated state of exactly the kind this
 * remediation exists to remove.
 *
 * FAIL CLOSED on anything else (S4g): a malformed wire status gets the
 * non-state glyph, same as the Matrix — never the paused one. The word label
 * comes from the SHARED `getStatusLabel` (actionPlanValueFormat), whose default
 * arm is "Status unavailable"; the local copy that answered "Not started" for a
 * malformed status was this view quietly disagreeing with every other surface.
 */
const getStatusIcon = (status: TaskStatusPersisted): string => {
  switch (status) {
    case 'completed': return '✅';
    case 'in_progress': return '🔄';
    case 'not_started': return '⏸️';
    case 'blocked': return '⛔';
    case 'skipped': return '⏭️';
    default: return '❓';
  }
};

// Sprint 9: Source level badge config (DC-446)
const getSourceConfig = (source?: string): { icon: string; label: string; className: string } => {
  switch (source) {
    case 'journey':
      return { icon: '🚂', label: 'Journey', className: 'source-journey' };
    case 'organization':
      return { icon: '🏢', label: 'Org', className: 'source-organization' };
    case 'merged':
      return { icon: '🔀', label: 'Merged', className: 'source-merged' };
    default:
      return { icon: '📋', label: 'Station', className: 'source-default' };
  }
};

// ============================================================================
// TASK CARD SUB-COMPONENT
// ============================================================================

const TaskCard: React.FC<{
  task: Task;
  dependencies: Dependency[];
  allTasks: Task[];
  /** Canonical selection (S4f-B): the card SHOWS it and offers a named way to set it. */
  selected: boolean;
  onTaskClick: (taskId: string) => void;
  onTaskStatusChange: (taskId: string, status: TaskStatus) => void;
}> = ({ task, dependencies, allTasks, selected, onTaskClick, onTaskStatusChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showCalculation, setShowCalculation] = useState(false);

  // Check for validation warnings
  const hasValidationWarnings = task.value_calculation?.validation_result &&
    !task.value_calculation.validation_result.is_valid;

  // Get task dependencies
  const taskDeps = dependencies.filter(d => d.to_task_id === task.id);
  const depTasks = taskDeps.map(dep =>
    allTasks.find(t => t.id === dep.from_task_id)
  ).filter(Boolean) as Task[];

  const SETTABLE: TaskStatus[] = ['not_started', 'in_progress', 'completed'];
  /**
   * `blocked` / `skipped` are persisted but NOT client-settable — and neither is
   * a malformed wire status. `nextStatus` is null for all of them, and a null
   * next status renders a STATIC indicator, not a button (S4g): the previous
   * shape was a button whose click silently did nothing, which reads as broken
   * rather than as "this state is not yours to change".
   */
  const nextStatus: TaskStatus | null = (SETTABLE as string[]).includes(task.status)
    ? SETTABLE[(SETTABLE.indexOf(task.status as TaskStatus) + 1) % SETTABLE.length]
    : null;

  return (
    // NOT a clickable div (S4h): expansion belongs to the named chevron button
    // below, which keyboard and assistive tech can actually operate.
    <div
      className={`task-card ${task.is_critical_path ? 'critical-path' : ''} status-${task.status}${selected ? ' is-selected' : ''}`}
      aria-current={selected ? 'true' : undefined}
    >
      {/* Card Header */}
      <div className="task-header">
        <div className="task-left">
          {nextStatus !== null ? (
            /*
             * The control names BOTH the task and the transition (S4g): an
             * icon-only toggle labelled "Status: not_started" told a screen-reader
             * user neither what would happen nor to which task, and told a sighted
             * user nothing at all. The visible word is the CURRENT state; the
             * action name says what clicking records.
             */
            <button
              type="button"
              className="status-toggle"
              aria-label={`Mark "${task.title}" as ${getStatusLabel(nextStatus)}`}
              title={`Mark "${task.title}" as ${getStatusLabel(nextStatus)}`}
              onClick={(e) => {
                e.stopPropagation();
                onTaskStatusChange(task.id, nextStatus);
              }}
            >
              <span aria-hidden="true">{getStatusIcon(task.status)}</span>
              <span className="ap-status-word">{getStatusLabel(task.status)}</span>
            </button>
          ) : (
            /*
             * Blocked, skipped, or a malformed wire status: visible as itself,
             * NOT operable. There is no client-side transition out of these
             * states, so no control claims one.
             */
            <span
              className={`status-toggle status-toggle--static status-${task.status}`}
              title={`${getStatusLabel(task.status)} — recorded on the plan; not changeable from this list`}
            >
              <span aria-hidden="true">{getStatusIcon(task.status)}</span>
              <span className="ap-status-word">{getStatusLabel(task.status)}</span>
            </span>
          )}

          <div className="task-title-section">
            {task.is_critical_path && <span className="critical-badge" title="Critical Path">⭐</span>}
            <h4 className="task-title">{task.title}</h4>
            {/* S4f-B: the named selection control — sets the CANONICAL selection
                (URL apTask), so the drawer opens and every view lands on this task. */}
            <button
              type="button"
              className="ap-list-details"
              data-ap-task-button={task.id}
              onClick={(e) => {
                e.stopPropagation();
                onTaskClick(task.id);
              }}
            >
              {`Open details: ${task.title}`}
            </button>
          </div>
        </div>

        <div className="task-right">
          {/* Sprint 9: Source level badge (DC-446) */}
          {(() => {
            const sourceConfig = getSourceConfig(task.source_level);
            return (
              <span
                className={`source-badge ${sourceConfig.className}`}
                title={`Source: ${sourceConfig.label}`}
              >
                <span className="source-icon">{sourceConfig.icon}</span>
                <span className="source-label">{sourceConfig.label}</span>
              </span>
            );
          })()}
          {hasValidationWarnings && (
            <span className="validation-warning-badge" title="Calculation has validation warnings">
              ⚠️
            </span>
          )}
          <span
            className="priority-badge"
            style={{ backgroundColor: getPriorityColor(task.priority) }}
          >
            {task.priority}
          </span>
          {/* The figure is the task's ANNUAL FORECAST — named, so it cannot be
              read as the 3-yr NPV shown inside the value derivation panel. */}
          <span className="value-badge" title="Annual forecast value">
            {formatCurrency(task.value_usd?.value ?? null)}
          </span>
          <button
            type="button"
            className="chevron task-expand"
            aria-expanded={isExpanded}
            aria-label={`${isExpanded ? 'Hide' : 'Show'} details for "${task.title}"`}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <span aria-hidden="true">{isExpanded ? '▼' : '▶'}</span>
          </button>
        </div>
      </div>

      {/* Card Body (Expanded) */}
      {isExpanded && (
        <div className="task-body">
          {/* Description */}
          <div className="task-section">
            <div className="section-label">Description</div>
            <p className="task-description">{task.description}</p>
          </div>

          {/* Implementation Steps */}
          {task.implementation_steps && task.implementation_steps.length > 0 && (
            <div className="task-section">
              <div className="section-label">Implementation Steps</div>
              <ul className="implementation-steps">
                {task.implementation_steps.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Metadata Grid */}
          <div className="task-metadata">
            <div className="metadata-item">
              <span className="metadata-label">Effort</span>
              <span className="metadata-value">
                {/* Never "0 weeks" for an absent estimate — see formatEffortWeeks. */}
                {task.effort_weeks === null
                  ? EFFORT_UNAVAILABLE_LABEL
                  : `${task.effort_weeks} weeks`}
              </span>
            </div>
            <div className="metadata-item">
              <span className="metadata-label">Impact</span>
              <span className="metadata-value">{task.impact}</span>
            </div>
            <div className="metadata-item">
              <span className="metadata-label">Timeline</span>
              <span className="metadata-value">
                {/*
                  * A week range is a SCHEDULE, and an unestimated task has none (2A
                  * follow-up). This rendered `W0 → W0` — or whatever range the stored
                  * document happened to carry — for a task nobody had estimated, which
                  * reads as a placed, zero-duration piece of work. Same fabrication as
                  * "0w", one column over.
                  */}
                {task.effort_weeks === null ? (
                  <span className="schedule--unavailable">{SCHEDULE_UNAVAILABLE_LABEL}</span>
                ) : (
                  `W${task.start_week} → W${task.end_week}`
                )}
              </span>
            </div>
            <div className="metadata-item">
              <span className="metadata-label">Station</span>
              <span className="metadata-value">{task.station_name}</span>
            </div>
          </div>

          {/* FAIR Rules */}
          {task.fair_rules && task.fair_rules.length > 0 && (
            <div className="task-section">
              <div className="section-label">FAIR Rules Addressed</div>
              <div className="fair-tags">
                {task.fair_rules.map(rule => (
                  <span key={rule} className="fair-tag">{rule}</span>
                ))}
              </div>
            </div>
          )}

          {/* Dependencies */}
          {depTasks.length > 0 && (
            <div className="task-section">
              <div className="section-label">Prerequisites</div>
              <div className="dependency-list">
                {depTasks.map(depTask => (
                  <div key={depTask.id} className="dependency-item">
                    <span className={`dep-status status-${depTask.status}`}>
                      <span title={getStatusLabel(depTask.status)} aria-label={getStatusLabel(depTask.status)} role="img">
                        {getStatusIcon(depTask.status)}
                      </span>
                    </span>
                    <span className="dep-title">{depTask.title}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Business Value Details - Enhanced with ViewCalculationPanel */}
          {task.value_calculation && (
            <div className="task-section value-calculation-section">
              <ViewCalculationPanel
                calculation={task.value_calculation}
                taskTitle={task.title}
                isExpanded={showCalculation}
                onToggle={() => setShowCalculation(!showCalculation)}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const ListView: React.FC<ListViewProps> = ({
  tasks,
  phases,
  dependencies,
  onTaskClick,
  onTaskStatusChange,
  selectedTaskId = null,
  filters,
  onClearFilters,
}) => {
  const [expandedPhases, setExpandedPhases] = useState<Set<string>>(
    new Set(phases.map(p => p.id)) // All phases expanded by default
  );

  /*
   * THE PLAN-WIDE FILTERS, CONSUMED IDENTICALLY TO THE MATRIX (S4f-B).
   * Visibility only: phase totals and progress stay derived from the full plan;
   * filtering decides which cards render, never what a phase claims about itself.
   */
  const isVisible = (t: Task): boolean => matchesFilters(t, filters);
  const visibleTotal = tasks.filter(isVisible).length;
  const filtersActive = anyFilterActive(filters);

  const togglePhase = (phaseId: string) => {
    const newExpanded = new Set(expandedPhases);
    if (newExpanded.has(phaseId)) {
      newExpanded.delete(phaseId);
    } else {
      newExpanded.add(phaseId);
    }
    setExpandedPhases(newExpanded);
  };

  // If no phases defined, show all tasks in a single "All Tasks" section
  if (!phases || phases.length === 0) {
    return (
      <div className="list-view">
        <div className="phase-accordion">
          <div className="phase-header phase-header-static">
            <div className="phase-left">
              <h3 className="phase-name">All Tasks</h3>
            </div>
            <div className="phase-right">
              <span className="phase-tasks-count">{tasks.length} tasks</span>
              <span className="phase-value">
                {/* Aggregation rule: one unpriced task and there is no total (S4d). */}
                {formatCurrency(sumTaskValues(tasks).total)}
              </span>
            </div>
          </div>
          <div className="phase-body">
            {tasks.filter(isVisible).length > 0 ? (
              tasks.filter(isVisible).map(task => (
                <TaskCard
                  key={task.id}
                  task={task}
                  dependencies={dependencies}
                  allTasks={tasks}
                  selected={selectedTaskId === task.id}
                  onTaskClick={onTaskClick}
                  onTaskStatusChange={onTaskStatusChange}
                />
              ))
            ) : (
              <div className="phase-empty">
                {tasks.length === 0 ? 'No tasks available' : 'No tasks match the filters'}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="list-view">
      {/* The same visible result count the Matrix shows — one filter state, one number. */}
      <p className="ap-view-count" data-testid="ap-list-result-count">
        Showing {visibleTotal} of {tasks.length} tasks
      </p>
      {filtersActive && visibleTotal === 0 && tasks.length > 0 && (
        <div className="ap-matrix-zero" role="status">
          <p>No tasks match the current filters.</p>
          <button type="button" className="ap-matrix-toolbar__clear" onClick={onClearFilters}>
            Clear filters
          </button>
        </div>
      )}
      {phases.map(phase => {
        const phaseTasks = tasks.filter(t => t.phase_id === phase.id);
        const visiblePhaseTasks = phaseTasks.filter(isVisible);
        const isExpanded = expandedPhases.has(phase.id);

        return (
          <div key={phase.id} className="phase-accordion">
            {/* Phase Header — the toggle is a NAMED button, not a clickable div
                (S4h). The heading stays outside the control, where a heading
                is valid and still navigable as one. */}
            <div className="phase-header">
              <div className="phase-left">
                <button
                  type="button"
                  className="phase-chevron"
                  aria-expanded={isExpanded}
                  aria-label={`${isExpanded ? 'Collapse' : 'Expand'} phase ${phase.name}`}
                  onClick={() => togglePhase(phase.id)}
                >
                  <span aria-hidden="true">{isExpanded ? '▼' : '▶'}</span>
                </button>
                <h3 className="phase-name">{phase.name}</h3>
                <span className="phase-timeline">
                  Week {phase.start_week} - {phase.end_week}
                </span>
              </div>

              <div className="phase-right">
                <span className="phase-tasks-count">
                  {filtersActive
                    ? `${visiblePhaseTasks.length} of ${phaseTasks.length} tasks`
                    : `${phaseTasks.length} tasks`}
                </span>
                <span className="phase-value">
                  {formatCurrency(phase.total_value_usd)}
                </span>
                <div className="phase-progress-mini">
                  {/*
                    * `phase.progress_pct.toFixed(0)` — unguarded, and the crash the conflict
                    * test recorded. The fix is not a `?? 0`: the server now overlays this from
                    * the phase's RENDERED tasks and sends `null` when a phase has no resolvable
                    * tasks, so `null` means unavailable and 0 would be a fabricated measurement.
                    */}
                  <span className="progress-text">
                    {phase.progress_pct === null
                      ? PROGRESS_UNAVAILABLE_LABEL
                      : `${phase.progress_pct.toFixed(0)}%`}
                  </span>
                </div>
              </div>
            </div>

            {/* Phase Body (Task Cards) */}
            {isExpanded && (
              <div className="phase-body">
                {visiblePhaseTasks.length > 0 ? (
                  visiblePhaseTasks.map(task => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      dependencies={dependencies}
                      allTasks={tasks}
                      selected={selectedTaskId === task.id}
                      onTaskClick={onTaskClick}
                      onTaskStatusChange={onTaskStatusChange}
                    />
                  ))
                ) : (
                  <div className="phase-empty">
                    {phaseTasks.length === 0
                      ? 'No tasks in this phase'
                      : 'No tasks in this phase match the filters'}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
